<template>
  <transition name="ts-fade">
    <div
      v-if="visible"
      class="ts-overlay"
      role="dialog"
      aria-modal="true"
      @click.self="close"
    >
      <!-- Aviones de papel -->
      <div class="plane plane-1">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 12L22 2L17 22L11 14L2 12Z" fill="#20b2aa" stroke="#1a8a83" stroke-width="0.5" stroke-linejoin="round"/>
          <path d="M11 14L17 22L13 14L11 14Z" fill="#178078"/>
        </svg>
      </div>
      <div class="plane plane-2">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 12L22 2L17 22L11 14L2 12Z" fill="#c6588d" stroke="#a84573" stroke-width="0.5" stroke-linejoin="round"/>
          <path d="M11 14L17 22L13 14L11 14Z" fill="#a84573"/>
        </svg>
      </div>
      <div class="plane plane-3">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 12L22 2L17 22L11 14L2 12Z" fill="#f8ac04" stroke="#d09003" stroke-width="0.5" stroke-linejoin="round"/>
          <path d="M11 14L17 22L13 14L11 14Z" fill="#b87f02"/>
        </svg>
      </div>

      <!-- Confeti -->
      <div class="confetti-container" ref="confetti"></div>

      <!-- Tarjeta principal -->
      <div class="success-card" ref="card">
        <button
          type="button"
          class="ts-close"
          aria-label="Cerrar"
          @click="close"
        >
          ×
        </button>

        <div class="watercolor-burst">
          <div class="burst-blob burst-blob-1"></div>
          <div class="burst-blob burst-blob-2"></div>
          <div class="burst-blob burst-blob-3"></div>
        </div>

        <div class="success-circle-wrapper">
          <div class="pulse-ring pulse-ring-1"></div>
          <div class="pulse-ring pulse-ring-2"></div>
          <div class="pulse-ring pulse-ring-3"></div>

          <div class="success-circle">
            <div class="fluid-layer fluid-1"></div>
            <div class="fluid-layer fluid-2"></div>
            <div class="fluid-layer fluid-3"></div>
            <div class="circle-shine"></div>
            <svg class="check-svg" viewBox="0 0 50 50">
              <path class="check-path" d="M14 26 L22 34 L38 16"/>
            </svg>
          </div>

          <div class="sparkle" style="top: -10px; left: -10px; width: 16px; height: 16px; animation-delay: 1s;">
            <svg viewBox="0 0 24 24" fill="#f8ac04"><path d="M12 2L14 10L22 12L14 14L12 22L10 14L2 12L10 10L12 2Z"/></svg>
          </div>
          <div class="sparkle" style="top: 10px; right: -15px; width: 12px; height: 12px; animation-delay: 1.3s;">
            <svg viewBox="0 0 24 24" fill="#c6588d"><path d="M12 2L14 10L22 12L14 14L12 22L10 14L2 12L10 10L12 2Z"/></svg>
          </div>
          <div class="sparkle" style="bottom: -5px; left: 20px; width: 14px; height: 14px; animation-delay: 1.6s;">
            <svg viewBox="0 0 24 24" fill="#20b2aa"><path d="M12 2L14 10L22 12L14 14L12 22L10 14L2 12L10 10L12 2Z"/></svg>
          </div>
          <div class="sparkle" style="bottom: 20px; right: -10px; width: 10px; height: 10px; animation-delay: 1.9s;">
            <svg viewBox="0 0 24 24" fill="#f8ac04"><path d="M12 2L14 10L22 12L14 14L12 22L10 14L2 12L10 10L12 2Z"/></svg>
          </div>
        </div>

        <h1 class="success-title">¡Genial! Pago procesado ✨</h1>
        <p class="success-subtitle">
          <template v-if="loading">Cargando los detalles de tu compra…</template>
          <template v-else-if="hasTransaction">Tu compra de {{ serviceName }} se confirmó con éxito</template>
          <template v-else>No encontramos transacciones recientes</template>
        </p>

        <div class="credits-box" v-if="hasTransaction">
          <div>
            <span class="credits-number" ref="creditsNumber">0</span>
          </div>
          <div class="credits-label">{{ creditsLabel }}</div>
          <div class="credits-total" v-if="totalFormatted">
            Total pagado: <strong>{{ totalFormatted }}</strong>
          </div>
          <div class="credits-celebration">¡A disfrutarlos! 🎉</div>
        </div>

        <button class="action-button" @click="close">
          ¡Genial!
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import transactionService from "@/services/transactionService";
import { trackPurchase } from "@/services/analytics";

const SERVICE_NAMES_BY_CODE = {
  EUR_STU: "Eureka",
  PLANI: "Planificaciones",
  UTP_STU: "Utopie",
  UTP_PLUS: "UtopiePlus",
  DOCUMENT: "Documentos PIE",
  MATERIAL: "Pruebas Informales"
};

const CONFETTI_COLORS = [
  "#20b2aa", "#84cec7",
  "#c6588d", "#e8b5d1",
  "#f8ac04", "#f9d38f",
  "#e84756", "#ecd5cd",
];

export default {
  name: "TransactionSuccess",
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      loaded: false,
      transaction: null,
      counterRaf: null,
    };
  },
  computed: {
    hasTransaction() {
      return !!this.transaction;
    },
    productCode() {
      return this.transaction ? this.transaction.product_code : null;
    },
    serviceName() {
      if (!this.productCode) return "tu servicio";
      return SERVICE_NAMES_BY_CODE[this.productCode] || this.productCode;
    },
    amount() {
      return this.transaction ? Number(this.transaction.amount) || 0 : 0;
    },
    creditsLabel() {
      const name = this.serviceName;
      return this.amount === 1
        ? `uso de ${name} agregado a tu cuenta`
        : `usos de ${name} agregados a tu cuenta`;
    },
    totalFormatted() {
      if (!this.transaction || this.transaction.total == null) return null;
      try {
        return new Intl.NumberFormat("cl-CL", {
          style: "currency",
          currency: "CLP",
        }).format(this.transaction.total);
      } catch (e) {
        return `$${this.transaction.total}`;
      }
    },
  },
  watch: {
    visible(open) {
      if (open) this.handleOpen();
      else this.cancelCounter();
    },
  },
  mounted() {
    if (this.visible) this.handleOpen();
  },
  beforeDestroy() {
    this.cancelCounter();
  },
  methods: {
    handleOpen() {
      this.$nextTick(() => this.generateConfetti());
      if (!this.loaded) this.loadTransaction();
      else if (this.hasTransaction) {
        this.$nextTick(() => this.animateCounter(this.amount));
      }
    },
    close() {
      this.$emit("close");
    },
    loadTransaction() {
      if (!this.$store.state.login.token) return;
      this.loading = true;
      transactionService
        .getLastTransaction()
        .then((data) => {
          this.transaction = data && data.id ? data : null;
          this.loading = false;
          this.loaded = true;
          if (this.hasTransaction) {
            // Acá es donde vuelve el usuario después de que Webpay autorizó:
            // el servidor redirige al SPA con ?transaction_success=1 y esta
            // es la primera pantalla que ve. trackPurchase descarta los
            // repetidos, porque ese parámetro sobrevive a una recarga.
            trackPurchase(this.transaction);
            this.$nextTick(() => this.animateCounter(this.amount));
          }
        })
        .catch(() => {
          this.loading = false;
          this.loaded = true;
          this.$toasted.error("No pudimos cargar tu última transacción.");
        });
    },
    cancelCounter() {
      if (this.counterRaf) {
        cancelAnimationFrame(this.counterRaf);
        this.counterRaf = null;
      }
    },
    animateCounter(target, duration = 1500) {
      this.cancelCounter();
      const el = this.$refs.creditsNumber;
      if (!el) return;
      el.textContent = "0";
      const startTime = performance.now();
      const startDelay = 600;
      const tick = (now) => {
        const elapsed = now - startTime - startDelay;
        if (elapsed < 0) {
          this.counterRaf = requestAnimationFrame(tick);
          return;
        }
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = Math.floor(eased * target);
        el.textContent = current.toLocaleString("es-CL");
        if (progress < 1) {
          this.counterRaf = requestAnimationFrame(tick);
        } else {
          el.textContent = target.toLocaleString("es-CL");
          el.style.transition =
            "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)";
          el.style.transform = "scale(1.15)";
          setTimeout(() => {
            el.style.transform = "scale(1)";
          }, 200);
        }
      };
      this.counterRaf = requestAnimationFrame(tick);
    },
    generateConfetti() {
      const container = this.$refs.confetti;
      if (!container) return;
      container.innerHTML = "";
      const total = 80;
      for (let i = 0; i < total; i++) {
        const piece = document.createElement("div");
        piece.className = "confetti";
        const color =
          CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
        const left = Math.random() * 100;
        const delay = Math.random() * 2.5;
        const duration = 3 + Math.random() * 2.5;
        const size = 6 + Math.random() * 8;
        const shape = Math.random();
        piece.style.left = left + "%";
        piece.style.background = color;
        piece.style.width = size + "px";
        piece.style.height = size * 1.4 + "px";
        piece.style.animationDelay = 0.7 + delay + "s";
        piece.style.animationDuration = duration + "s";
        if (shape < 0.33) {
          piece.style.borderRadius = "50%";
        } else if (shape < 0.66) {
          piece.style.borderRadius = "2px";
        } else {
          piece.style.borderRadius = "0";
          piece.style.transform = "rotate(" + Math.random() * 360 + "deg)";
        }
        container.appendChild(piece);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ts-overlay {
  position: fixed;
  inset: 0;
  z-index: 1080;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(20, 24, 38, 0.55);
  backdrop-filter: blur(4px);
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  overflow: hidden;
}

.ts-fade-enter-active,
.ts-fade-leave-active {
  transition: opacity 0.25s ease;
}
.ts-fade-enter,
.ts-fade-leave-to {
  opacity: 0;
}

.success-card {
  position: relative;
  width: 100%;
  max-width: 480px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  padding: 50px 40px 40px;
  text-align: center;
  overflow: hidden;
  animation: cardEntrance 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #20b2aa 0%, #c6588d 50%, #f8ac04 100%);
  }
}

.ts-close {
  position: absolute;
  top: 12px;
  right: 16px;
  z-index: 5;
  background: transparent;
  border: none;
  color: #95a5a6;
  font-size: 1.75rem;
  line-height: 1;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 50%;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #34495e;
  }
}

@keyframes cardEntrance {
  0% { opacity: 0; transform: translateY(40px) scale(0.9); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

.watercolor-burst {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 400px;
  height: 400px;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 0;
  opacity: 0;
  animation: burstFade 1.2s ease-out 0.2s both;
}

@keyframes burstFade {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.3); }
  50% { opacity: 0.5; }
  100% { opacity: 0.25; transform: translate(-50%, -50%) scale(1); }
}

.burst-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(30px);
  animation: blobFloat 8s ease-in-out infinite;
}
.burst-blob-1 {
  width: 200px; height: 200px; top: 10%; left: 15%;
  background: radial-gradient(circle, rgba(32, 178, 170, 0.6) 0%, transparent 70%);
}
.burst-blob-2 {
  width: 180px; height: 180px; top: 30%; right: 10%;
  background: radial-gradient(circle, rgba(198, 88, 141, 0.5) 0%, transparent 70%);
  animation-delay: -2s;
}
.burst-blob-3 {
  width: 160px; height: 160px; bottom: 15%; left: 35%;
  background: radial-gradient(circle, rgba(248, 172, 4, 0.5) 0%, transparent 70%);
  animation-delay: -4s;
}

@keyframes blobFloat {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(20px, -15px) scale(1.1); }
  66% { transform: translate(-15px, 20px) scale(0.95); }
}

.success-circle-wrapper {
  position: relative;
  width: 140px;
  height: 140px;
  margin: 0 auto 30px;
  z-index: 2;
  animation: circlePopIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s both;
}

@keyframes circlePopIn {
  0% { transform: scale(0) rotate(-90deg); opacity: 0; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}

.success-circle {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(32, 178, 170, 0.35);
}

.fluid-layer {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  animation: fluidMove 6s ease-in-out infinite;
}
.fluid-1 {
  background: radial-gradient(ellipse at 30% 30%,
    rgba(32, 178, 170, 0.95) 0%,
    rgba(32, 178, 170, 0.8) 30%,
    rgba(26, 138, 131, 0.6) 60%,
    rgba(26, 138, 131, 0.4) 100%);
  transform-origin: 30% 40%;
}
.fluid-2 {
  background: radial-gradient(ellipse at 70% 60%,
    rgba(198, 88, 141, 0.5) 0%,
    rgba(198, 88, 141, 0.3) 40%,
    transparent 80%);
  animation-delay: -2s;
  transform-origin: 70% 60%;
  mix-blend-mode: overlay;
}
.fluid-3 {
  background: radial-gradient(ellipse at 50% 80%,
    rgba(248, 172, 4, 0.5) 0%,
    rgba(248, 172, 4, 0.3) 40%,
    transparent 80%);
  animation-delay: -4s;
  transform-origin: 50% 80%;
  mix-blend-mode: overlay;
}

@keyframes fluidMove {
  0%, 100% {
    transform: scale(1) rotate(0deg);
    border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
  }
  25% {
    transform: scale(1.1) rotate(90deg);
    border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%;
  }
  50% {
    transform: scale(0.95) rotate(180deg);
    border-radius: 70% 30% 40% 60% / 40% 70% 60% 30%;
  }
  75% {
    transform: scale(1.05) rotate(270deg);
    border-radius: 40% 70% 60% 30% / 70% 40% 30% 60%;
  }
}

.circle-shine {
  position: absolute;
  top: 15%; left: 20%;
  width: 35%; height: 35%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.5) 0%, transparent 70%);
  border-radius: 50%;
  z-index: 3;
}

.check-svg {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 70px; height: 70px;
  z-index: 4;
}

.check-path {
  fill: none;
  stroke: white;
  stroke-width: 8;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: drawCheck 0.6s cubic-bezier(0.65, 0, 0.45, 1) 0.7s forwards;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
}

@keyframes drawCheck {
  to { stroke-dashoffset: 0; }
}

.pulse-ring {
  position: absolute;
  top: 50%; left: 50%;
  border-radius: 50%;
  border: 3px solid;
  transform: translate(-50%, -50%);
  opacity: 0;
  width: 100%; height: 100%;
}
.pulse-ring-1 {
  border-color: rgba(32, 178, 170, 0.5);
  animation: pulseOut 2.5s ease-out 1s infinite;
}
.pulse-ring-2 {
  border-color: rgba(198, 88, 141, 0.4);
  animation: pulseOut 2.5s ease-out 1.5s infinite;
}
.pulse-ring-3 {
  border-color: rgba(248, 172, 4, 0.4);
  animation: pulseOut 2.5s ease-out 2s infinite;
}

@keyframes pulseOut {
  0% { width: 100%; height: 100%; opacity: 0.8; }
  100% { width: 220%; height: 220%; opacity: 0; }
}

.success-title {
  font-size: 1.7rem;
  font-weight: 700;
  margin-bottom: 12px;
  background: linear-gradient(135deg, #20b2aa 0%, #c6588d 50%, #f8ac04 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: titleFadeIn 0.6s ease-out 1s both,
    gradientShift 4s ease-in-out 1.6s infinite;
  position: relative;
  z-index: 2;
}

@keyframes titleFadeIn {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.success-subtitle {
  font-size: 1rem;
  color: #7f8c8d;
  margin-bottom: 30px;
  position: relative;
  z-index: 2;
  opacity: 0;
  animation: textFadeIn 0.5s ease-out 1.2s forwards;
}

@keyframes textFadeIn {
  to { opacity: 1; }
}

.credits-box {
  position: relative;
  z-index: 2;
  background: linear-gradient(135deg,
    rgba(32, 178, 170, 0.08) 0%,
    rgba(198, 88, 141, 0.08) 50%,
    rgba(248, 172, 4, 0.08) 100%);
  border: 2px dashed rgba(32, 178, 170, 0.3);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 28px;
  opacity: 0;
  animation: textFadeIn 0.5s ease-out 1.4s forwards;
}

.credits-number {
  font-size: 3.2rem;
  font-weight: 800;
  line-height: 1;
  background: linear-gradient(135deg, #20b2aa, #1a8a83);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: inline-block;
}

.credits-label {
  font-size: 0.95rem;
  color: #5a6c7d;
  margin-top: 6px;
  font-weight: 500;
}

.credits-total {
  font-size: 0.9rem;
  color: #5a6c7d;
  margin-top: 10px;
}

.credits-celebration {
  font-size: 0.85rem;
  color: #c6588d;
  margin-top: 8px;
  font-weight: 600;
}

.action-button {
  position: relative;
  z-index: 2;
  background: linear-gradient(135deg, #20b2aa, #1a8a83);
  color: white;
  border: none;
  padding: 14px 36px;
  border-radius: 30px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  font-family: inherit;
  box-shadow: 0 6px 20px rgba(32, 178, 170, 0.4);
  transition: all 0.3s ease;
  opacity: 0;
  animation: buttonFadeIn 0.5s ease-out 1.7s forwards;
}

@keyframes buttonFadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.action-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(32, 178, 170, 0.5);
}

.action-button:active {
  transform: translateY(-1px);
}

.confetti-container {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  overflow: hidden;
}

::v-deep .confetti {
  position: absolute;
  width: 10px;
  height: 14px;
  top: -20px;
  opacity: 0;
  animation: confettiFall linear forwards;
}

@keyframes confettiFall {
  0% { opacity: 1; transform: translateY(0) rotateZ(0deg) rotateX(0deg); }
  100% { opacity: 0.8; transform: translateY(110vh) rotateZ(720deg) rotateX(360deg); }
}

.plane {
  position: absolute;
  width: 32px;
  height: 32px;
  pointer-events: none;
  z-index: 2;
  opacity: 0;
}

.plane svg {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
}

.plane-1 {
  top: 25%; left: -50px;
  animation: planeFly1 4s ease-in-out 0.8s forwards;
}
.plane-2 {
  top: 60%; right: -50px;
  animation: planeFly2 4.5s ease-in-out 1.2s forwards;
}
.plane-3 {
  top: 15%; right: -50px;
  animation: planeFly3 3.8s ease-in-out 1.5s forwards;
}

@keyframes planeFly1 {
  0% { opacity: 0; transform: translate(0, 0) rotate(15deg); }
  15% { opacity: 1; }
  50% { transform: translate(50vw, -50px) rotate(20deg); }
  85% { opacity: 1; }
  100% { opacity: 0; transform: translate(110vw, 100px) rotate(35deg); }
}

@keyframes planeFly2 {
  0% { opacity: 0; transform: translate(0, 0) rotate(195deg); }
  15% { opacity: 1; }
  50% { transform: translate(-50vw, 30px) rotate(200deg); }
  85% { opacity: 1; }
  100% { opacity: 0; transform: translate(-110vw, -80px) rotate(215deg); }
}

@keyframes planeFly3 {
  0% { opacity: 0; transform: translate(0, 0) rotate(200deg); }
  15% { opacity: 1; }
  100% { opacity: 0; transform: translate(-110vw, 200px) rotate(225deg); }
}

.sparkle {
  position: absolute;
  pointer-events: none;
  z-index: 5;
  opacity: 0;
  animation: sparkleAnimation 2s ease-in-out infinite;
}

.sparkle svg {
  width: 100%;
  height: 100%;
}

@keyframes sparkleAnimation {
  0%, 100% { opacity: 0; transform: scale(0); }
  50% { opacity: 1; transform: scale(1); }
}

@media (max-width: 500px) {
  .success-card { padding: 40px 24px 32px; }
  .success-title { font-size: 1.4rem; }
  .credits-number { font-size: 2.6rem; }
}
</style>
